/**
 * 会话票据（Alice 页面 tickets 的复刻）：登录会话的有效率、令牌复用告警、按账号明细与最近登录事件。
 *
 * 背景：Alice 观测的是 Alice 客户端票据（强制模式、版本、设备号）。本仓没有客户端票据，对应物是账号服务的
 *   浏览器会话：一次登录一条会话链，刷新令牌轮换，旧令牌被重复使用即判定被盗并作废整条链。接口是
 *   /admin/identity/session-tickets，数据来自 sessions 表与审计。「强制模式」改为会话时长策略，「老版本占比」改为
 *   令牌复用告警，「版本」列改为登录 IP 数，「设备」列改为登录端（控制台或管理后台）。登录失败不写审计，看不到。
 * 设计意图：版式与 Alice 一致；窗口天数可选（Alice 是进程内存里自启动起累计，本仓是真实历史，所以加了窗口）。
 * 关键约束：只读；一次请求最多 100 个账号、200 条事件，工作量与窗口内的会话数成正比。
 */
import { useState } from 'react'
import { runtime2Api, type SessionState } from '@ap/api-client/admin-runtime2'
import { AG_runtime2 as G, errorText2 } from '@ap/i18n/admin-runtime2'
import { useI18n } from '@ap/i18n'
import { fmtDateTime, navigate, useLoad } from '@ap/ui'
import { OpsNote, OpsPage, Stat, Stats, TableWrap, Toolbar } from '../../alice/kit.tsx'
import { EmptyRow, Section, ToolSelect, ellipsis } from '../../alice/kit-runtime2.tsx'

const STATE_COLOR: Record<string, string> = {
  active: '#10b981',
  revoked: '#8b5cf6',
  expired: '#6b7280',
  reuse_detected: '#dc2626',
}

const RESULT_COLOR: Record<string, string> = { success: '#10b981', revoked: '#8b5cf6', reuse_detected: '#dc2626' }

function Tag({ color, children }: { color: string; children: string }) {
  return (
    <span className="badge" style={{ background: `${color}18`, color }}>
      {children}
    </span>
  )
}

export function Tickets() {
  const { t, locale } = useI18n()
  const [days, setDays] = useState('7')
  const { data, error, loading } = useLoad(() => runtime2Api.sessionTickets(Number(days)), [days])
  const dash = t(G.common.dash)
  const pct = (n: number) => (data && data.total ? `${((n * 100) / data.total).toFixed(1)}%` : dash)
  const failed = error ? t(G.common.loadFailed, { msg: errorText2(error, locale) }) : null

  return (
    <OpsPage eyebrow={t(G.tickets.eyebrow)} title={t(G.tickets.title)} desc={t(G.tickets.desc)} chip={t(G.tickets.chip)}>
      <Toolbar>
        <ToolSelect value={days} onChange={(e) => setDays(e.target.value)} options={['1', '7', '30', '90'].map((n) => ({ value: n, label: t(G.tickets.window, { n }) }))} />
      </Toolbar>

      <Stats>
        <Stat
          label={t(G.tickets.statPolicy)}
          value={data ? t(G.tickets.statPolicyValue, { c: Math.round(data.policy.console_ttl_hours / 24), a: data.policy.admin_ttl_hours }) : dash}
          sub={data ? t(G.tickets.statPolicySub, { ca: Math.round(data.policy.console_access_ttl_s / 60), aa: Math.round(data.policy.admin_access_ttl_s / 60) }) : undefined}
        />
        <Stat
          label={t(G.tickets.statTotal)}
          value={data ? data.total.toLocaleString() : dash}
          sub={data ? t(G.tickets.statTotalSub, { n: data.window_days, a: data.accounts }) : undefined}
        />
        <Stat label={t(G.tickets.statValid)} value={data ? pct(data.active) : dash} color="success" sub={t(G.tickets.statValidSub)} />
        <Stat
          label={t(G.tickets.statReuse)}
          value={data ? data.reuse_events.toLocaleString() : dash}
          color={data && data.reuse_events > 0 ? 'error' : undefined}
          sub={data ? t(G.tickets.statReuseSub, { a: data.reuse_accounts }) : undefined}
        />
      </Stats>

      <Section title={t(G.tickets.distTitle)} copy={t(G.tickets.distCopy)}>
        <div style={{ padding: '16px 20px' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, fontSize: 12 }}>
            {failed ? (
              <div style={{ color: 'var(--error-text)' }}>{failed}</div>
            ) : !data || data.total === 0 ? (
              <div style={{ color: 'var(--text-tertiary)' }}>{loading ? t(G.common.loading) : t(G.tickets.distEmpty)}</div>
            ) : (
              (['active', 'revoked', 'expired'] as const)
                .map((k) => [k, data[k]] as const)
                .sort((a, b) => b[1] - a[1])
                .map(([k, v]) => {
                  const p = (v * 100) / data.total
                  return (
                    <div key={k} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <div style={{ width: 150, flexShrink: 0 }}>
                        {t(G.tickets.state[k])} <span className="mono" style={{ color: 'var(--text-tertiary)' }}>{k}</span>
                      </div>
                      <div style={{ flex: 1, height: 16, background: 'var(--bg-muted)', borderRadius: 4, overflow: 'hidden' }}>
                        <div style={{ width: `${Math.max(p, 0.5)}%`, height: '100%', background: STATE_COLOR[k] }} />
                      </div>
                      <div style={{ width: 110, textAlign: 'right', flexShrink: 0 }}>
                        {v.toLocaleString()} · {p.toFixed(1)}%
                      </div>
                    </div>
                  )
                })
            )}
          </div>
          <div style={{ marginTop: 12, fontSize: 12, color: 'var(--text-tertiary)', lineHeight: 1.7 }}>
            {t(G.tickets.distLegend, { n: data ? data.rotated.toLocaleString() : 0 })}
          </div>
        </div>
      </Section>

      <Section title={t(G.tickets.usersTitle)} copy={t(G.tickets.usersCopy)}>
        <TableWrap
          head={
            <>
              <th>{t(G.tickets.colUser)}</th>
              <th>{t(G.tickets.colState)}</th>
              <th>{t(G.tickets.colDist)}</th>
              <th>{t(G.tickets.colIp)}</th>
              <th>{t(G.tickets.colIps)}</th>
              <th>{t(G.tickets.colUa)}</th>
              <th>{t(G.tickets.colActive)}</th>
            </>
          }
        >
          {failed ? (
            <EmptyRow cols={7}>{failed}</EmptyRow>
          ) : !data || data.users.length === 0 ? (
            <EmptyRow cols={7}>{loading ? t(G.common.loading) : t(G.tickets.usersEmpty)}</EmptyRow>
          ) : (
            data.users.map((u) => {
              const st: SessionState = u.status
              return (
                <tr key={u.account_id} style={{ cursor: 'pointer' }} onClick={() => navigate(`/accounts/${u.account_id}`)}>
                  <td>
                    <div style={{ fontWeight: 500 }}>{u.name || u.email}</div>
                    <div className="mono" style={{ color: 'var(--text-tertiary)', fontSize: 11 }}>
                      {u.email}
                    </div>
                  </td>
                  <td>{st ? <Tag color={STATE_COLOR[st] ?? '#6b7280'}>{t(G.tickets.state[st])}</Tag> : dash}</td>
                  <td className="mono" style={{ fontSize: 11 }}>
                    {t(G.tickets.distCell, { a: u.active, r: u.revoked, x: u.expired })}
                    {u.reuse_events > 0 && <div style={{ color: '#dc2626' }}>{t(G.tickets.reuseCell, { n: u.reuse_events })}</div>}
                  </td>
                  <td className="mono" style={{ fontSize: 12 }}>
                    {u.last_ip || dash}
                  </td>
                  <td style={{ fontSize: 12 }}>{u.ips}</td>
                  <td style={{ fontSize: 11, color: 'var(--text-tertiary)', maxWidth: 220, ...ellipsis }} title={u.user_agent}>
                    {u.user_agent || dash}
                  </td>
                  <td style={{ whiteSpace: 'nowrap', fontSize: 12 }}>{fmtDateTime(u.last_active, locale)}</td>
                </tr>
              )
            })
          )}
        </TableWrap>
      </Section>

      <Section title={t(G.tickets.shakesTitle)} copy={t(G.tickets.shakesCopy)}>
        <TableWrap
          head={
            <>
              <th>{t(G.tickets.colTime)}</th>
              <th>{t(G.tickets.colUser)}</th>
              <th>{t(G.tickets.colResult)}</th>
              <th>{t(G.tickets.colReason)}</th>
              <th>IP</th>
              <th>{t(G.tickets.colKind)}</th>
              <th>{t(G.tickets.colUa)}</th>
            </>
          }
        >
          {failed ? (
            <EmptyRow cols={7}>{failed}</EmptyRow>
          ) : !data || data.handshakes.length === 0 ? (
            <EmptyRow cols={7}>{loading ? t(G.common.loading) : t(G.tickets.shakesEmpty)}</EmptyRow>
          ) : (
            data.handshakes.map((h) => (
              <tr key={h.id}>
                <td style={{ whiteSpace: 'nowrap', fontSize: 12 }}>{fmtDateTime(h.time, locale)}</td>
                <td>
                  <div style={{ fontWeight: 500, fontSize: 12 }}>{h.name || h.email || h.account_id}</div>
                </td>
                <td>
                  <Tag color={RESULT_COLOR[h.result] ?? '#6b7280'}>{t(G.tickets.result[h.result])}</Tag>
                </td>
                <td style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{h.reason ? t(G.tickets.reason[h.reason] ?? G.tickets.reason['session.refresh_reuse']) : dash}</td>
                <td className="mono" style={{ fontSize: 12 }}>
                  {h.ip || dash}
                </td>
                <td style={{ fontSize: 12 }}>{t(G.tickets.kind[h.kind])}</td>
                <td style={{ fontSize: 11, color: 'var(--text-tertiary)', maxWidth: 200, ...ellipsis }} title={h.user_agent}>
                  {h.user_agent || dash}
                </td>
              </tr>
            ))
          )}
        </TableWrap>
      </Section>
      <OpsNote>{t(G.tickets.noFailedLogin)}</OpsNote>
    </OpsPage>
  )
}
